// src/pages/login/Login.tsx
import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import AircallWorkspace from 'aircall-everywhere';
import { setPhoneVisible, setPhone } from '../../store/reducers/phoneSlice';
import { setLogin, setTester } from '../../store/reducers/userSlice';
import { Settings } from '../../models/Settings';
import '../../assets/styles/demo.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useGetAircallUserQuery, useGetOperatorsQuery,useGetSetupQuery  } from '../../api/endpoints/apiConfig';
import { useAuth } from '../login/AuthContext';
import packageJson from '../../../package.json';
import {
    AIRCALL_USER_ID_SESSION_KEY,
    notifyAircallUserIdSessionChanged,
} from '../../utils/callsIdSessionSync';

const Login: React.FC = () => {
    const dispatch = useAppDispatch();
    const phoneVisible = useAppSelector((state) => state.phone.phoneVisible);
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>('');
    const [shouldFetchUser, setShouldFetchUser] = useState<boolean>(false);
    const { login } = useAuth();
    // Fetch operators data using RTK Query
    const { data: operatorsData } = useGetOperatorsQuery();
    // Fetch user data based on email
    const { data: aircallUser } = useGetAircallUserQuery(email, {
        skip: !shouldFetchUser || !email.trim(),
    });
    // Fetch setup data using RTK Query
    const { data: setupData } = useGetSetupQuery();
    // Store setup data in session storage when loaded
    useEffect(() => {
        if (setupData) {
            sessionStorage.setItem('setupData', JSON.stringify(setupData));
        }
    }, [setupData]);
    // Store operators data in session storage when loaded
    useEffect(() => {
        if (operatorsData) {
            sessionStorage.setItem('operators', JSON.stringify(operatorsData));
        }
    }, [operatorsData]);
    const handleLogin = () => {
        dispatch(setPhoneVisible(true));
        const newPhone = new AircallWorkspace({
            domToLoadWorkspace: '#phone',
            onLogin: (settings: Settings) => {
                // Dispatch login action
                login();
                dispatch(setLogin(true));
                sessionStorage.setItem('isLogin', 'true');
                const userEmail = settings.user.email;
                const firstName = settings.user.first_name;
                sessionStorage.setItem('FirstName', firstName);
                const lastName = settings.user.last_name;
                sessionStorage.setItem('LastName', lastName);
                if (userEmail) {
                    setEmail(userEmail); // Set the email
                    setShouldFetchUser(true); // Trigger the API call
                } else {
                    toast.error('Email is not available from Aircall settings.');
                }
            },
            onLogout: () => {
                dispatch(setLogin(false));
                dispatch(setPhoneVisible(false));
                sessionStorage.setItem('isLogin', 'false');
                sessionStorage.removeItem(AIRCALL_USER_ID_SESSION_KEY);
                notifyAircallUserIdSessionChanged();
                navigate('/');
                toast.info('You have been signed out. See you soon!', { toastId: 'logout' });
            },
        });
        dispatch(setPhone(newPhone));
    };
    useEffect(() => {
        if (shouldFetchUser) {
        }
    }, [shouldFetchUser, email]);
    useEffect(() => {
        if (aircallUser && aircallUser.user && aircallUser.user.length > 0) {
            const testerValue = aircallUser.user[0].tester;
            dispatch(setTester(aircallUser.user[0].tester));
            sessionStorage.setItem('tester', testerValue.toString());
            const aircallTableUserId = aircallUser.user[0].user_id;
            if (aircallTableUserId != null) {
                sessionStorage.setItem(AIRCALL_USER_ID_SESSION_KEY, String(aircallTableUserId));
                notifyAircallUserIdSessionChanged();
            }

            navigate('/WaitingPage');
        } else if (aircallUser && aircallUser.user.length === 0) {
            // Handle case when no user is found
            toast.error('Not Authorised');
            setShouldFetchUser(false); // Stop trying to fetch

            // Invalidate the session and log out completely
            dispatch(setLogin(false));
            dispatch(setPhoneVisible(false));
        }
    }, [aircallUser, dispatch, navigate]);
    return (
        <>
            <div id="phone-container" className={phoneVisible ? '' : 'd-none'} style={{ position: 'absolute', zIndex: 9999 }}>
                <div className="arrow-up"></div>
                <div id="phone"></div>
            </div>
            <Box
                height="100vh"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="#f0f2f5"
                padding="20px"
            >
                <Box
                    width="100%"
                    maxWidth="400px"
                    bgcolor="white"
                    padding="30px"
                    borderRadius="8px"
                    boxShadow="0px 4px 12px rgba(0, 0, 0, 0.1)"
                >
                    <Typography variant="h4" marginBottom="20px" textAlign="center">
                        Storage 360
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ marginTop: '20px' }}
                        onClick={handleLogin}
                    >
                        Login To AirCall
                    </Button>
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ marginTop: '16px' }}>
                        Version: {packageJson.version}
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

export default Login;
